<script setup lang="ts">
import { type FragmentType, graphql } from '@/gql';
import type { DiagnosisResultPersonalityType } from '@/gql/graphql';
import {
  type PersonalityId,
  convertPersonalityIdToDiagnosisResultPersonalityType,
} from '@/types/app/diagnosis/personality';
import UserThumbnail from 'theme/components/atoms/UserThumbnail.vue';
import RatingStars from 'theme/components/molecules/RatingStars.vue';
import UserInputText from 'theme/components/molecules/UserInputText.vue';

const props = defineProps<{
  review: FragmentType<typeof PlannerDetailReviewSummaryFragment>;
}>();

const PlannerDetailReviewSummaryFragment = graphql(`
  fragment PlannerDetailReviewSummary on Review {
    id
    score
    comment
    updatedAt
    user2 {
      id
      gender
      birthYear
    }
  }
`);

const review = computed(() =>
  getFragment(PlannerDetailReviewSummaryFragment, props.review)
);

const profile = computed((): string => {
  const { gender, birthYear } = review.value.user2;
  return getUserProfileString(gender, birthYear);
});

const userThumbnailId = computed((): Maybe<DiagnosisResultPersonalityType> => {
  const numId = parseInt(
    review.value.user2.id.replace(/[^0-9]/g, '').substring(0, 5)
  );

  return convertPersonalityIdToDiagnosisResultPersonalityType(
    ((numId % 8) + 1) as PersonalityId
  );
});

const dateString = computed((): string =>
  formatJapaneseDateWithoutWeek(fromDateString(review.value.updatedAt))
);
</script>

<template>
  <section :class="$style.container">
    <header :class="$style.header">
      <UserThumbnail
        :class="$style.thumbnail"
        :personality-type="userThumbnailId"
        :alt="profile"
      />
      <p :class="[$style.header__profile, $style.profile]">{{ profile }}</p>
      <p :class="[$style.header__date, $style.date]">{{ dateString }}</p>
    </header>
    <section :class="$style.review">
      <RatingStars
        :class="$style.stars"
        :rate="review.score"
        :show-rate-number="true"
      />
      <UserInputText
        v-if="review.comment !== ''"
        :class="$style.comment"
        :text="review.comment"
      />
    </section>
  </section>
</template>

<style lang="scss" module>
.header {
  display: flex;
  align-items: center;

  &__profile {
    margin-left: 12px;
  }

  &__date {
    margin-left: auto;
  }
}

.profile {
  font-size: 14px;
  font-weight: bold;
}

.comment {
  padding: 16px;
  font-size: 14px;
}

.review {
  margin-top: 12px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-rounder;
  padding: 20px;
}

.thumbnail {
  width: 38px;
}

.stars {
  font-size: 20px;
}
</style>
